import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './HeroNeon.module.css';

// Same file index.html preloads, so the buffer is already warm when React mounts.
// Trimmed from the Squarespace original: the opening shot is cut and the audio
// track dropped, since the hero is always muted — 14.1MB and 10.0s down to
// 6.1MB and 8.7s. Rebuild with scripts/ or re-trim from the source if it changes.
const HERO_VIDEO = '/hero/hero.mp4';

/**
 * Fullscreen video hero with the neon script wordmark in the lower third.
 *
 * Replaces HeroIris (the lens/iris hero), which is left intact in this folder —
 * see the import in pages/Home.jsx to switch back.
 */
export default function HeroNeon({ videoSrc = HERO_VIDEO, name = 'Drew Hallé', onContact }) {
  const videoRef = useRef(null);

  // Autoplay can be refused before any interaction; retry on the first gesture
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    window.addEventListener('pointerdown', tryPlay, { once: true });
    return () => window.removeEventListener('pointerdown', tryPlay);
  }, []);

  return (
    <div style={{
      position: 'relative', width: '100%', height: '100%',
      overflow: 'hidden', background: '#050505',
    }}>
      <video
        ref={videoRef}
        src={videoSrc}
        autoPlay muted loop playsInline preload="auto"
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'cover',          /* fill the viewport, crop as needed */
          background: '#050505',
        }}
      />

      {/* Legibility wash — darkest at the bottom, where the wordmark sits */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background:
          'linear-gradient(180deg, rgba(4,4,4,0.42) 0%, rgba(4,4,4,0.10) 34%, rgba(4,4,4,0.55) 74%, rgba(4,4,4,0.88) 100%)',
      }} />

      {/* Buttons + scroll hint — bottom centre */}
      <div className={styles.bottom}>
        {/* Not drawn any more, but kept so the page still has its heading */}
        <h1 className={styles.srOnly}>{name}</h1>

        <nav className={styles.ctas} aria-label="Explore">
          <Link to="/photography" className={styles.cta}>Photography</Link>
          <Link to="/videography" className={styles.cta}>Videography</Link>
          <button type="button" className={styles.cta} onClick={onContact}>Contact</button>
        </nav>

        <div className={styles.hint}>
          SCROLL DOWN
          <svg className={styles.hintArrow} width="11" height="13" viewBox="0 0 11 13"
            fill="none" stroke="currentColor" strokeWidth="1.4"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="5.5" y1="0.9" x2="5.5" y2="11.4" />
            <polyline points="1.4,7.4 5.5,11.6 9.6,7.4" />
          </svg>
        </div>
      </div>
    </div>
  );
}
